/**
 * Configuration centralisée des modules métier d'un dossier.
 *
 * Cette structure permet d'ajouter un nouveau module (ex: Assurances, Photovoltaïque)
 * sans reconstruire la page dossier : ajouter une entrée ici + un composant de section.
 *
 * ⚠️ Les `key` doivent CORRESPONDRE aux clés backend historiques (easy_power, chantier)
 * pour éviter toute migration DB / régression.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * COMMENT AJOUTER UN NOUVEAU MODULE (ex: Photovoltaïque)
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * ÉTAPE 1 — Backend
 *   Ajouter les champs dans le modèle Prospect côté backend :
 *     - `pv_stage`, `pv_stage_changed_at`, `pv_disabled`, `pv_loss_reason`
 *   Ajouter les étapes possibles (pv_prospection, pv_devis, pv_signe, pv_gagne, pv_perdu…)
 *   dans la config backend `PIPELINE_STAGES`.
 *
 * ÉTAPE 2 — Frontend labels
 *   Dans `moduleLabels.js`, ajouter :
 *     pv: 'Photovoltaïque',
 *     pv_short: 'PV',
 *     pv_lost: 'Photovoltaïque — Affaire perdue',
 *
 * ÉTAPE 3 — Ce fichier
 *   Ajouter une entrée dans MODULES_CONFIG (voir exemple commenté ci-dessous).
 *
 * ÉTAPE 4 — Composant de section
 *   Créer `PhotovoltaicSection.jsx` dans /components/prospect/ et l'inclure
 *   dans la page ProspectDetail dans une nouvelle <TabsContent value="pv">.
 *
 * ÉTAPE 5 — Route de tâche (optionnel)
 *   Si le module reçoit des tâches, ajouter la clé "pv" dans les module filters
 *   pour que `nextActionInfo.pv` soit renseigné.
 *
 * C'est tout — la tuile, la nav sticky, l'onglet, la bannière de perte se
 * gèrent automatiquement grâce à cette config.
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import { Handshake, Wrench } from 'lucide-react';
import { MODULE_LABELS } from './moduleLabels';

export const MODULES_CONFIG = [
    {
        key: 'easy_power',
        label: MODULE_LABELS.easy_power,
        shortLabel: MODULE_LABELS.easy_power_short,
        icon: Handshake,
        color: 'amber',           // teinte Tailwind (amber-100/700 etc.)
        accent: 'from-amber-500 to-orange-500',
        // Champs backend liés au module (utile pour lire le statut sans hard-code)
        stageField: 'easy_power_stage',
        disabledField: 'easy_power_disabled',
        stageChangedAt: 'easy_power_stage_changed_at',
        lossReasonField: 'ep_loss_reason',
        // Ordre d'affichage
        order: 1,
    },
    {
        key: 'chantier',
        label: MODULE_LABELS.chantier,
        shortLabel: MODULE_LABELS.chantier_short,
        icon: Wrench,
        color: 'blue',
        accent: 'from-blue-500 to-indigo-500',
        stageField: 'chantier_stage',
        disabledField: 'chantier_disabled',
        stageChangedAt: 'chantier_stage_changed_at',
        lossReasonField: 'ch_loss_reason',
        order: 2,
    },
    // ─── EXEMPLE — décommenter pour ajouter le module Photovoltaïque ───
    // {
    //     key: 'pv',
    //     label: 'Photovoltaïque',
    //     shortLabel: 'PV',
    //     icon: Sun,                            // à importer de lucide-react
    //     color: 'yellow',
    //     accent: 'from-yellow-500 to-amber-500',
    //     stageField: 'pv_stage',
    //     disabledField: 'pv_disabled',
    //     stageChangedAt: 'pv_stage_changed_at',
    //     lossReasonField: 'pv_loss_reason',
    //     order: 3,
    // },
];

/** Utilitaire : retourne la config d'un module par sa clé. */
export function getModuleConfig(key) {
    return MODULES_CONFIG.find(m => m.key === key);
}

/** Utilitaire : liste des modules actifs sur un prospect. */
export function getActiveModules(prospect) {
    if (!prospect) return [];
    return MODULES_CONFIG.filter(m => {
        const stage = prospect[m.stageField];
        const disabled = prospect[m.disabledField];
        return !!stage && !disabled;
    });
}

/** Utilitaire : liste des modules disponibles (activables) mais pas encore actifs. */
export function getInactiveModules(prospect) {
    if (!prospect) return MODULES_CONFIG;
    return MODULES_CONFIG.filter(m => {
        const stage = prospect[m.stageField];
        const disabled = prospect[m.disabledField];
        return !stage || disabled;
    });
}
